import React, { useMemo, useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Badge } from "@shared/ui/badge";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { Tabs } from "@shared/ui/tabs";
import {
  useRoutes,
  useMeterDispatchDetail,
  useMeterRunDetail,
  useReaderEfficiencySummary,
  useReaderEfficiencyList,
} from "@features/mx/reads/hook";
import {
  useAssignConsumer as useAssignConsumerReads,
  useMeterReaderList as useMeterReaderListReads,
} from "@features/mx/meterReads/hooks";
import { mapToLabelValue } from "@features/mx/device-management/utils";
import { RoutesTab } from "./RoutesTab";
import {
  User,
  TrendingUp,
  CheckCircle2,
  Star,
} from "lucide-react";

export const MobileRoutesTab: React.FC = () => {
  const { runId } = useParams();
  const remoteUtilityId = useRemoteUtilityId();

  const [searchParams] = useSearchParams();
  const searchQuery = decodeURIComponent(searchParams.get("search") || "");

  // Modal control states
  const [assignModalOpen, setAssignModalOpen] = useState<boolean>(false);
  const [unassignModalOpen, setUnassignModalOpen] = useState<boolean>(false);
  const [selectedDispatchId, setSelectedDispatchId] = useState<number | null>(null);
  const [selectedRouteInfo, setSelectedRouteInfo] = useState<{
    route: string;
    assignedTo: string;
  } | null>(null);
  const [initialReaderIds, setInitialReaderIds] = useState<string[]>([]);

  // Fetch cycle details to get cycleId
  const { data: meterRunDetail } = useMeterRunDetail(runId, remoteUtilityId);
  const cycleId = meterRunDetail?.meterSchedule?.meterCycle?.id;

  // Fetch dispatch data to pass refetch to modals
  const { refetch } = useMeterDispatchDetail({
    meter_run_id: runId,
    remote_utility_id: remoteUtilityId,
    page: 1,
    origin: "Web"
  });

  const { data: efficiencySummaryResponse } = useReaderEfficiencySummary(
    remoteUtilityId,
    runId
  );

  const { data: efficiencyListResponse, isLoading: efficiencyListLoading } = useReaderEfficiencyList(
    1,
    remoteUtilityId,
    runId,
    100
  );

  // Fetch active readers for cards
  const { data: meterAssignData } = useAssignConsumerReads({
    remote_utility_id: remoteUtilityId,
    isActive: "True",
    page: 1,
  });

  const { data: readersFilter } = useMeterReaderListReads(remoteUtilityId);
  const readersList = mapToLabelValue(readersFilter, "name", "id");

  const handleAssign = (row: any) => {
    setSelectedDispatchId(Number(row.id));
    setSelectedRouteInfo({
      route: row.route,
      assignedTo: row.assignedReader || "—",
    });
    setInitialReaderIds([]);
    setAssignModalOpen(true);
  };

  const handleReAssign = (row: any) => {
    setSelectedDispatchId(Number(row.id));
    setSelectedRouteInfo({
      route: row.route,
      assignedTo: row.assignedReader || "—",
    });
    const matchingReader = readersList?.find(
      (r: any) => r.label === row.assignedReader
    );
    setInitialReaderIds(matchingReader ? [String(matchingReader.value)] : []);
    setAssignModalOpen(true);
  };

  const handleUnassign = (row: any) => {
    setSelectedDispatchId(Number(row.id));
    setSelectedRouteInfo({
      route: row.route,
      assignedTo: row.assignedReader || "—",
    });
    setUnassignModalOpen(true);
  };

  // Available Readers (top-level cards) mapping
  const availableReaders = useMemo(() => {
    if (!meterAssignData?.results) return [];
    return meterAssignData.results.slice(0, 3).map((r: any) => ({
      id: r.id,
      name: r.name,
      availability: r.availability || "Available",
      assignedRoutes: r.assignedRoutes || 0,
      services: r.services || ["Water"],
    }));
  }, [meterAssignData]);

  // Derived metrics for Reader Efficiency tab
  const efficiencyMetrics = useMemo(() => {
    const summary = efficiencySummaryResponse?.result || {};
    return {
      totalReaders: summary.totalReaders || 0,
      avgCompletion: summary.avgCompletion || 0,
      readsCollected: summary.readsCollected || 0,
      topPerformer: summary.topPerformer?.readerName || "—",
    };
  }, [efficiencySummaryResponse]);

  // Derived Reader Efficiency list
  const efficiencyList = useMemo(() => {
    const rawResults = efficiencyListResponse?.results || [];
    return rawResults.map((r: any) => ({
      id: r.readerId || r.employeeId || r.readerName,
      reader: r.readerName,
      routes: r.routes || 0,
      totalMeters: r.totalMeters || 0,
      completed: r.completed || 0,
      pending: r.pending || 0,
      exceptions: r.exceptions || 0,
      completionPercentage: r.completionPercentage || 0,
      avgPerDay: r.avgPerDay || 0,
      onTimePercentage: r.onTimePercentage || 0,
      photos: r.photos || 0,
      lastActivity: r.lastActivity || "—",
    }));
  }, [efficiencyListResponse]);

  // Filter efficiency data by search input
  const filteredEfficiencyList = useMemo(() => {
    if (!searchQuery.trim()) return efficiencyList;
    return efficiencyList.filter((r) =>
      r.reader.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [efficiencyList, searchQuery]);

  // Columns for Efficiency Table
  const efficiencyColumns: TableColumn<any>[] = [
    {
      key: "reader",
      header: "Reader",
      render: (val) => (
        <div className="flex items-center gap-1.5 text-xs text-gray-700">
          <User className="w-3.5 h-3.5 text-gray-400 shrink-0" />
          <span className="font-semibold text-gray-900">{val}</span>
        </div>
      ),
    },
    { key: "routes", header: "Routes" },
    { key: "totalMeters", header: "Total Meters" },
    { key: "completed", header: "Completed" },
    { key: "pending", header: "Pending" },
    { key: "exceptions", header: "Exceptions" },
    {
      key: "completionPercentage",
      header: "Completion %",
      render: (val) => (
        <div className="flex items-center gap-2">
          <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden shrink-0">
            <div
              className={`h-full rounded-full ${
                val >= 100 ? "bg-emerald-500" : "bg-amber-500"
              }`}
              style={{ width: `${val}%` }}
            />
          </div>
          <span className="font-medium text-xs">{val}%</span>
        </div>
      ),
    },
    { key: "avgPerDay", header: "Avg / Day" },
    {
      key: "onTimePercentage",
      header: "On-Time %",
      render: (val) => <span className="font-semibold text-emerald-600">{val}%</span>,
    },
    { key: "photos", header: "Photos" },
    { key: "lastActivity", header: "Last Activity" },
  ];

  // Tab Components definition for the shared Tabs service
  const tabComponents = {
    assign: {
      label: "Assign",
      icon: <User className="h-4 w-4" />,
      component: (
        <div className="space-y-6 p-4">
          {/* Top Cards: Available Readers */}
          {availableReaders.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-gray-500 tracking-wider">
                Available Readers
              </h4>
              <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-thin">
                {availableReaders.map((reader: any) => {
                  const isAvailable = reader.availability?.toLowerCase() === "available";
                  return (
                    <div
                      key={reader.id}
                      className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-start gap-3 w-72 shrink-0"
                    >
                      <div className="w-10 h-10 rounded-full bg-teal-50 text-teal-600 flex items-center justify-center shrink-0">
                        <User className="w-5 h-5" />
                      </div>
                      <div className="space-y-1 min-w-0 flex-1">
                        <h4 className="text-sm font-semibold text-gray-900 truncate">
                          {reader.name}
                        </h4>
                        <p className="text-xs text-gray-500 truncate">
                          {reader.services?.join(", ") || "Water"}
                        </p>
                        <div className="flex items-center gap-2 pt-1">
                          <Badge
                            variant="outline"
                            className={`${
                              isAvailable
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                : "bg-amber-50 text-amber-700 border-amber-200"
                            } text-[10px] font-medium px-2 py-0.5 rounded-full`}
                          >
                            {reader.availability}
                          </Badge>
                          <span className="text-[11px] text-gray-400">
                            • {reader.assignedRoutes} active
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Routes Table - reusing RoutesTab component! */}
          <RoutesTab
            isMobileAssign={true}
            onAssign={handleAssign}
            onReassign={handleReAssign}
            onUnassign={handleUnassign}
          />
        </div>
      ),
    },
    efficiency: {
      label: "Reader Efficiency",
      icon: <TrendingUp className="h-4 w-4" />,
      component: (
        <div className="space-y-6 p-4">
          {/* Metrics summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500">Total Readers</p>
                <p className="text-2xl font-bold text-gray-900">
                  {efficiencyMetrics.totalReaders}
                </p>
              </div>
              <div className="w-8 h-8 rounded-lg bg-gray-50 text-gray-400 flex items-center justify-center">
                <User className="w-4 h-4" />
              </div>
            </div>

            <div className="bg-emerald-50/30 p-4 rounded-xl border border-emerald-100 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-emerald-800/70">Avg Completion</p>
                <p className="text-2xl font-bold text-emerald-950">
                  {efficiencyMetrics.avgCompletion}%
                </p>
              </div>
              <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <TrendingUp className="w-4 h-4" />
              </div>
            </div>

            <div className="bg-emerald-50/30 p-4 rounded-xl border border-emerald-100 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-emerald-800/70">Reads Collected</p>
                <p className="text-2xl font-bold text-emerald-950">
                  {efficiencyMetrics.readsCollected}
                </p>
              </div>
              <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4" />
              </div>
            </div>

            <div className="bg-amber-50/30 p-4 rounded-xl border border-amber-100 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-amber-800/70">Top Performer</p>
                <p className="text-base font-bold text-amber-600 truncate max-w-[150px]">
                  {efficiencyMetrics.topPerformer}
                </p>
              </div>
              <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-500 flex items-center justify-center">
                <Star className="w-4 h-4" />
              </div>
            </div>
          </div>

          {/* Efficiency Table */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <GenericTable
              data={filteredEfficiencyList}
              columns={efficiencyColumns}
              loading={efficiencyListLoading}
              rowKey="id"
              searchPlaceholder="Search routes..."
              emptyMessage="No readers found matching your criteria"
            />
          </div>
        </div>
      ),
    },
  };

  return (
    <div className="space-y-6 pt-2">
      <div className="border rounded-md overflow-hidden bg-white p-1">
        <Tabs
          tabComponents={tabComponents}
          defaultValue="assign"
          urlMapping={{
            assign: "assign",
            efficiency: "efficiency",
          }}
          tabsListClassName="w-full grid grid-cols-2 bg-muted/30 p-1"
          idPrefix="mobile-routes-tab"
          disableUrlSync={true}
        />
      </div>

      {/* Assignment Modal (extracted) */}
      <AssignReaderModal
        assignOpen={assignModalOpen}
        unassignOpen={unassignModalOpen}
        onAssignClose={() => setAssignModalOpen(false)}
        onUnassignClose={() => setUnassignModalOpen(false)}
        selectedDispatchId={selectedDispatchId}
        selectedRouteInfo={selectedRouteInfo}
        initialReaderIds={initialReaderIds}
        refetch={refetch}
      />
    </div>
  );
};

// Helper hook to retrieve remoteUtilityId inside component
const useRemoteUtilityId = () => {
  return useRemoteUtilityIdReads();
};

import { useRemoteUtilityId as useRemoteUtilityIdReads } from "@shared/selectors/globalSelectors";
import { AssignReaderModal } from "../dialogs/AssignReaderModal";
export default MobileRoutesTab;
